import { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import MissYouButton from './MissYouButton';
import MessageSection from './MessageSection';
import CalendarView from './CalendarView';
// import RelationshipTimer from './RelationshipTimer';
import CompactRelationshipTimer from './CompactRelationshipTimer'; // Alternative compact version
// import Timeline from './Timeline'; // Alternative visual timeline
import Milestones from './Milestones'; // Standalone milestones component
import NotificationToast from './Notification';
import BackgroundHearts from './BackgroundHearts';
import { RELATIONSHIP_CONFIG } from '../relationshipConfig';
import type { CalendarEvent, Message, Theme } from '../types';

interface PersonPageProps {
  person: 'tim' | 'diya';
}

interface NotificationData {
  show: boolean;
  message: string;
}

export const PersonPage = ({ person }: PersonPageProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [notification, setNotification] = useState<NotificationData>({ show: false, message: '' });

  const otherPerson = person === 'tim' ? 'diya' : 'tim';
  
  // Theme colors
  const theme: Theme = person === 'diya' 
    ? {
        primary: 'from-purple-500 to-pink-500',
        secondary: 'bg-purple-100',
        button: 'bg-purple-500 hover:bg-purple-600',
        text: 'text-purple-900',
        accent: 'text-pink-500'
      }
    : {
        primary: 'from-blue-500 to-teal-500',
        secondary: 'bg-blue-100',
        button: 'bg-blue-500 hover:bg-blue-600',
        text: 'text-blue-900',
        accent: 'text-teal-500'
      };

  // Listen for calendar events
  useEffect(() => {
    const q = query(
      collection(db, 'events'),
      orderBy('date', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventData: CalendarEvent[] = [];
      snapshot.forEach((doc) => {
        eventData.push({ id: doc.id, ...doc.data() } as CalendarEvent);
      });
      setEvents(eventData);
    });

    return () => unsubscribe();
  }, []);

  // Listen for messages
  useEffect(() => {
    const q = query(
      collection(db, 'messages'),
      orderBy('timestamp', 'asc'),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messageData: Message[] = [];
      snapshot.forEach((doc) => {
        messageData.push({ id: doc.id, ...doc.data() } as Message);
      });
      setMessages(messageData);
    });

    return () => unsubscribe();
  }, []);

  // Listen for "I miss you" pings
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'pings', otherPerson), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const lastPing = data.lastPing?.toDate();
        const now = new Date();
        
        if (lastPing && (now.getTime() - lastPing.getTime()) < 5000) {
          showNotification(`💕 ${otherPerson} misses you!`);
        }
      }
    });

    return () => unsubscribe();
  }, [otherPerson]);

  const showNotification = (message: string) => {
    setNotification({ show: true, message });
    setTimeout(() => {
      setNotification({ show: false, message: '' });
    }, 3000);
  };

  const handleMissYou = async () => {
    try {
      await setDoc(doc(db, 'pings', person), {
        lastPing: Timestamp.now(),
        from: person
      });
      showNotification('Sent! 💕');
    } catch (error) {
      console.error('Error sending ping:', error);
      showNotification('Failed to send');
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;

    try {
      await addDoc(collection(db, 'messages'), {
        text: text.trim(),
        from: person,
        timestamp: Timestamp.now()
      });
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleAddEvent = async (event: Omit<CalendarEvent, 'id'>) => {
    try {
      await addDoc(collection(db, 'events'), event);
      showNotification('Event added! 📅');
    } catch (error) {
      console.error('Error adding event:', error);
      showNotification('Failed to add event');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteDoc(doc(db, 'events', eventId));
      showNotification('Event deleted');
    } catch (error) {
      console.error('Error deleting event:', error);
      showNotification('Failed to delete event');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <BackgroundHearts />

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className={`text-center mb-12 bg-gradient-to-r ${theme.primary} rounded-3xl p-8 shadow-xl`}>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-2">
            {person === 'diya' ? 'For My Love 💜' : 'From My Love 💙'}
          </h1>
          <p className="text-white text-lg opacity-90">
            Happy Valentine's Day!
          </p>
        </div>

        {/* Notification */}
        {notification.show && (
          <NotificationToast message={notification.message} />
        )}

        {/* Miss You Button */}
        <div className="mb-8">
          <MissYouButton onClick={handleMissYou} theme={theme} />
        </div>

        {/* Relationship Timer */}
        {/* <div className="mb-8">
          <RelationshipTimer 
            theme={theme}
            officialDate={RELATIONSHIP_CONFIG.officialDate}
            unofficialDate={RELATIONSHIP_CONFIG.unofficialDate}
          />
        </div> */}

        {/* OPTION 2: Compact Timer */}
        <div className="mb-8">
          <CompactRelationshipTimer 
            theme={theme}
            officialDate={RELATIONSHIP_CONFIG.officialDate}
            unofficialDate={RELATIONSHIP_CONFIG.unofficialDate}
          />
        </div>
       

        {/* OPTION 3: Visual Timeline (shows all milestones chronologically) */}
        {/* 
        <div className="mb-8">
          <Timeline theme={theme} />
        </div>
        */}

        {/* Milestones */} 
        <div className="mb-8">
          <Milestones theme={theme} />
        </div>

        {/* Calendar */}
        <div className="mb-8">
          <CalendarView 
            events={events}
            onAddEvent={handleAddEvent}
            onDeleteEvent={handleDeleteEvent}
            currentPerson={person}
            theme={theme}
          />
        </div>

        {/* Messages */}
        <div className="mb-8">
          <MessageSection 
            messages={messages}
            currentPerson={person}
            onSendMessage={handleSendMessage}
            theme={theme}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonPage;